import ApexLogo from "@/assets/apex-logo.svg";
import { amplifyConfig } from "@/config/amplify";
import { useAuth } from "@/context/AuthContext";
import { Description } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { alpha, Stack } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function TopNav() {
  const { user, signOutUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get environment from config
  const environment = amplifyConfig.environment.toUpperCase();
  const showEnvBadge = ["TST", "DEV", "QA", "UAT"].includes(environment);

  // Determine active page
  const isDocumentsPage =
    location.pathname === "/dashboard" || location.pathname.startsWith("/documents");
  const isRulesPage = location.pathname === "/rules";

  const handleSignOut = async () => {
    await signOutUser();
    navigate("/login");
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <nav className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6 dark:border-[#232f48] dark:bg-[#111722]">
      {/* Logo and IntelliDoc Button */}
      <div className="flex items-center gap-4">
        <img src={ApexLogo} alt="Apex" className="h-10 w-auto" />

        {/* IntelliDoc App Selector Button */}
        <Button
          disableRipple
          sx={{
            color: (theme) => alpha(theme.palette.primary.contrastText, 0.56),
            ":hover": {
              color: (theme) => theme.palette.primary.contrastText,
              backgroundColor: "transparent",
            },
            textTransform: "uppercase",
            height: 64,
            p: 2,
            cursor: "default",
            pointerEvents: "none",
          }}
          startIcon={
            <Stack
              sx={{
                p: 0.5,
                bgcolor: "#1976d2", // Primary blue accent color
                borderRadius: "50%",
              }}
            >
              <Description sx={{ color: "white", fontSize: "1rem" }} />
            </Stack>
          }
        >
          <Typography fontSize={20} fontWeight={700} letterSpacing={0.8} color="#1976d2">
            INTELLIDOC
          </Typography>
        </Button>

        {/* Environment Badge */}
        {showEnvBadge && (
          <span className="rounded-md bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            {environment}
          </span>
        )}

        {/* Navigation Links */}
        <nav className="ml-8 flex h-16 items-center gap-1">
          <button
            onClick={() => navigate("/dashboard")}
            className={`flex h-full items-center border-b-2 px-4 text-sm font-bold transition-colors ${
              isDocumentsPage
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            }`}
          >
            Documents
          </button>
          <button
            onClick={() => navigate("/rules")}
            className={`flex h-full items-center border-b-2 px-4 text-sm font-bold transition-colors ${
              isRulesPage
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            }`}
          >
            Rules
          </button>
        </nav>
      </div>

      {/* Right side - User menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex items-center gap-2 rounded border border-gray-300 px-2 py-1.5 hover:bg-gray-50 transition-colors dark:border-gray-600 dark:hover:bg-gray-800"
          aria-label={isMenuOpen ? "Close user menu" : "Open user menu"}
          aria-expanded={isMenuOpen}
          aria-haspopup="true"
        >
          <img src={ApexLogo} alt="Apex Logo" className="h-8 w-auto" />
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 dark:bg-gray-600">
            <span className="material-symbols-outlined text-gray-600 dark:text-gray-300 text-xl">
              person
            </span>
          </div>
        </button>

        {/* Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute right-0 top-full mt-1 w-80 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 z-50">
            {/* User Info */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="material-symbols-outlined text-4xl text-gray-400 dark:text-gray-500">
                account_circle
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name || "User"}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {user?.email || ""}
                </span>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <button
                disabled
                className="w-full px-4 py-2 text-left text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed"
              >
                Profile Settings
              </button>
              <button
                onClick={handleSignOut}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Log Out
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
